import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FinchatMessagePayload, MessageAttachments, SessionId, toolType } from "../../assets/interfaces/interfaces";

type ChartDataType = {
  chart_type: string;
  keys: Array<unknown>;
  data: Array<unknown>;
  chart_title: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
};
type MetadataType = {
  chat_title?: string;
  chart_flag?: boolean;
  chart_data?: ChartDataType[];
  attachments?: MessageAttachments;
  list_of_tickers?: string[];
};
export type FinChatMessage = {
  message_id: string;
  chart_data: ChartDataType[];
  attachments?: MessageAttachments;
  chart_flag: boolean;
  input: string;
  output: string;
  outputLoading: boolean; // used for streaming output
  chat_id: string;
  list_of_tickers: string[];
  toolLoaders: Record<toolType, { completed: boolean; text: string; status: "loading" | "done" | "blocked" }>; // store tool loaders and whether they are completed
  purchaseCards: PurchaseCard[];
};

interface FinchatState {
  finchatMessages: Record<SessionId, FinChatMessage>;
  loading: boolean;
}

export type toolInfoType = {
  tool: string;
  text: string;
  status: "loading" | "done" | "blocked";
};

export type PurchaseCard = {
  bundle_id: string;
  bundle_name: string;
  price: { amount: number | null; currency: string; interval: string; note?: string };
  blocked_tools: string[];
  headline: string;
  body: string;
  cta_label: string;
  purchase_url: string;
};

const initialState: FinchatState = {
  finchatMessages: {},
  loading: false,
};

const makeEmptyFinChatMessage = () => {
  return {
    chart_data: [],
    chart_flag: false,
    input: "",
    output: "",
    outputLoading: false,
    chat_id: "",
    message_id: "",
    list_of_tickers: [],
    toolLoaders: {},
    purchaseCards: [],
  };
};

export const finchatSlice = createSlice({
  name: "finchat",
  initialState,
  reducers: {
    clearFinchatData: () => initialState,
    finchatMessageLoad: (
      state,
      action: PayloadAction<FinchatMessagePayload>,
    ) => {
      state.loading = true;
      state.finchatMessages[action.payload.session_id] = state.finchatMessages[action.payload.session_id] || makeEmptyFinChatMessage();
      const finchatmessage = state.finchatMessages[action.payload.session_id];
      finchatmessage.outputLoading = true;
      finchatmessage.output = "";
      finchatmessage.input = action.payload.input;
      finchatmessage.chat_id = action.payload.chat_id;
      finchatmessage.chart_data = [];
      if (action.payload.attachments) {
        finchatmessage.attachments = action.payload.attachments;
      }
      finchatmessage.list_of_tickers = [];
      finchatmessage.toolLoaders = {};
      finchatmessage.purchaseCards = [];
    },
    updateFinchatOutput: (
      state,
      action: PayloadAction<{ session_id: string; output: string }>,
    ) => {
      const message = state.finchatMessages[action.payload.session_id];
      if (message) {
        message.output += action.payload.output;
      }
    },
    markToolStarted: (
      state,
      action: PayloadAction<{ session_id: string; toolInfo: toolInfoType }>,
    ) => {
      state.finchatMessages[action.payload.session_id].toolLoaders[action.payload.toolInfo.tool] = {
        completed: false,
        text: action.payload.toolInfo.text,
        status: action.payload.toolInfo.status ?? "loading",
      };
    },

    markToolCompleted: (
      state,
      action: PayloadAction<{ session_id: string; toolInfo: toolInfoType }>,
    ) => {
      state.finchatMessages[action.payload.session_id].toolLoaders[action.payload.toolInfo.tool] = {
        completed: true,
        text: action.payload.toolInfo.text,
        status: action.payload.toolInfo.status ?? "done",
      };
    },

    addPurchaseCard: (
      state,
      action: PayloadAction<{ session_id: string; card: PurchaseCard }>,
    ) => {
      const message = state.finchatMessages[action.payload.session_id];
      if (message && !message.purchaseCards.some(c => c.bundle_id === action.payload.card.bundle_id)) {
        message.purchaseCards.push(action.payload.card);
      }
    },
    updateMetadata(state, action: PayloadAction<{ session_id: string; metadata: MetadataType }>) {
      const currentMessage = state.finchatMessages[action.payload.session_id];
      currentMessage.chart_flag = action.payload.metadata.chart_flag;
      currentMessage.chart_data = action.payload.metadata.chart_data;
      currentMessage.attachments = action.payload.metadata.attachments;
      currentMessage.list_of_tickers = action.payload.metadata.list_of_tickers;
    },
    finalizeFinchatMessage: (
      state,
      action: PayloadAction<{ session_id: string }>,
    ) => {
      const message = state.finchatMessages[action.payload.session_id];
      if (message) {
        message.outputLoading = false;
      }
      state.loading = false;
    },
    finchatMessageLoadFailure: (state) => {
      state.loading = false;
    },
    finchatMessageCancel: (state, action: PayloadAction<SessionId>) => {
      if (state.finchatMessages[action.payload]) {
        state.finchatMessages[action.payload].outputLoading = false;
      }
      state.loading = false;
    },
    finchatMessageCancelAll: (state) => {
      state.loading = false;
      for (const sessionId in state.finchatMessages) {
        if (Object.prototype.hasOwnProperty.call(state.finchatMessages, sessionId)) {
          state.finchatMessages[sessionId].outputLoading = false;
        }
      }
    },
  },
});

export const {
  clearFinchatData,
  finchatMessageLoad,
  updateFinchatOutput,
  updateMetadata,
  finalizeFinchatMessage,
  markToolStarted,
  markToolCompleted,
  addPurchaseCard,
  finchatMessageCancel,
  finchatMessageCancelAll,
  finchatMessageLoadFailure,
} = finchatSlice.actions;
export default finchatSlice.reducer;
